import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function CoLab() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/ux-ui-design');
  return <div className="w-full min-h-screen bg-white" data-id="element-681">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-682">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-cyan-400 transition-colors" data-id="element-683">
            <XIcon className="w-8 h-8" data-id="element-684" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-685" />
        </motion.div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-686">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-687">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-688">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-689" />
            <span className="font-light" data-id="element-690">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-691">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-cyan-50/30 to-stone-50" data-id="element-692">
        <div className="max-w-5xl mx-auto" data-id="element-693">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-694">
            <p className="text-cyan-600 font-light tracking-wide uppercase mb-4" data-id="element-695">
              Creative Direction, UX/UI Design
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-696">
              Co-Lab
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-697">
              An innovative beauty and wellness-oriented digital marketplace and
              gamified miniApp
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-698">
              <p className="text-lg" data-id="element-699">
                <span className="font-normal text-stone-900" data-id="element-700">Role:</span> UX
                Director, Creative Direction
              </p>
              <p className="text-lg" data-id="element-701">
                <span className="font-normal text-stone-900" data-id="element-702">Platform:</span>{' '}
                Web (Desktop & Mobile), Telegram miniApp
              </p>
              <p className="text-lg" data-id="element-703">
                <span className="font-normal text-stone-900" data-id="element-704">Year:</span> 2024
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-705">
        <div className="max-w-5xl mx-auto" data-id="element-706">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-707">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-708">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-709">
              Co-Lab represents a pioneering approach to wellness commerce,
              combining immersive 3D experiences with gamification mechanics to
              create an engaging digital marketplace for beauty and wellness
              products. As UX Director, I led the user experience strategy
              across multiple touchpoints.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer mb-8" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/Frame%20260%20(2).png')} data-id="element-710">
              <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-711">
                <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/Frame%20260%20(2).png" alt="Co-Lab project overview visual" className="w-full h-auto" data-id="element-712" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-713">
                  <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-714" />
                </div>
              </div>
            </motion.div>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-715">
              The project's core innovation lies in its multi-layered engagement
              model: users discover Co-Lab through marketing channels, explore
              and purchase products via an interactive, gamified website
              experience, and maintain ongoing engagement through a Telegram
              miniApp that rewards healthy habits with product discounts.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-716">
              The vision was to transform traditional e-commerce into an
              experiential journey where purchasing decisions are informed by
              immersive product exploration, while the miniApp creates a
              continuous relationship between the brand and users' wellness
              journeys.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Website Design - Desktop & Mobile */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-717">
        <div className="max-w-7xl mx-auto" data-id="element-718">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-719">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-720">
              Final Website Design
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-721">
              The website design creates an immersive wellness marketplace
              experience across all devices, balancing visual appeal with
              intuitive navigation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start" data-id="element-722">
              {/* Desktop - Takes 3 columns */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/%D0%9A%D0%B0%D1%80%D1%82%D0%BE%D1%87%D0%BA%D0%B0%20%D0%BF%D1%80%D0%BE%D0%B4%D1%83%D0%BA%D1%82%D0%B0%20(1).png')} data-id="element-723">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-724">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/%D0%9A%D0%B0%D1%80%D1%82%D0%BE%D1%87%D0%BA%D0%B0%20%D0%BF%D1%80%D0%BE%D0%B4%D1%83%D0%BA%D1%82%D0%B0%20(1).png" alt="Co-Lab Desktop Website" className="w-full h-auto object-contain" data-id="element-725" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-726">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-727" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-728">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-729">
                    Desktop Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-730">
                    Full website layout with immersive product discovery and
                    clear navigation
                  </p>
                </div>
              </motion.div>
              {/* Mobile - Takes 1 column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/colab%20mobile%20card%20product.png')} data-id="element-731">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-732">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/colab%20mobile%20card%20product.png" alt="Co-Lab Mobile Website" className="w-full h-auto object-contain" data-id="element-733" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-734">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-735" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-736">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-737">
                    Mobile Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-738">
                    Optimized responsive layout for seamless mobile shopping
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Mobile UX Development */}
      <section className="py-20 px-6 bg-white" data-id="element-739">
        <div className="max-w-7xl mx-auto" data-id="element-740">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-741">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-742">
              Mobile Experience Design Process
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-743">
              The mobile experience required extensive UX iteration to ensure
              the immersive qualities of the desktop experience translated
              seamlessly to smaller screens. This comprehensive design system
              maps every user journey, interaction pattern, and edge case.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start" data-id="element-744">
              {/* Main UX Flow - Takes 3 columns */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/scenario%201.3%20(2)%20(2).png')} data-id="element-745">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-746">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/scenario%201.3%20(2)%20(2).png" alt="Co-Lab Mobile UX Development" className="w-full h-auto" data-id="element-747" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-748">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-749" />
                  </div>
                </div>
              </motion.div>
              {/* Account Flow - Takes 1 column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/account%20(2).png')} data-id="element-750">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-751">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/account%20(2).png" alt="Co-Lab Account Flow" className="w-full h-auto" data-id="element-752" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-753">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-754" />
                  </div>
                </div>
              </motion.div>
            </div>
            <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-755">
              Comprehensive mobile UX flow mapping showing all user scenarios
              and interaction patterns
            </p>
          </motion.div>
        </div>
      </section>
      {/* 3D Immersive Website */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-756">
        <div className="max-w-7xl mx-auto" data-id="element-757">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-758">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-759">
              3D Immersive Website Concept
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-760">
              The immersive 3D website concept transforms product discovery into
              an experiential journey. Set in a serene natural landscape, users
              navigate through an interactive environment where products are
              discovered organically, creating a memorable and engaging shopping
              experience that reinforces the brand's connection to nature and
              wellness.
            </p>
            {/* 3D Graphics Work */}
            <div className="mb-16" data-id="element-761">
              <h3 className="text-2xl font-light text-stone-900 mb-3" data-id="element-762">
                3D Environment Design
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-763">
                Collaborating with 3D gaming graphics designers, we created
                immersive natural environments that blend product discovery with
                exploration.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-764">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/Untitled%20design%20(3).png')} data-id="element-765">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-[400px]" data-id="element-766">
                    <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/Untitled%20design%20(3).png" alt="3D Environment - Interactive Product Display" className="w-full h-full object-cover" data-id="element-767" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-768">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-769" />
                    </div>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/HighresScreenshot00017%204%20(1).png')} data-id="element-770">
                  <div className="relative overflow-hidden rounded-xl shadow-lg h-[400px]" data-id="element-771">
                    <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/HighresScreenshot00017%204%20(1).png" alt="3D Environment - Immersive Exploration Space" className="w-full h-full object-cover" data-id="element-772" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-773">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-774" />
                    </div>
                  </div>
                </motion.div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-775">
                3D environment renders showcasing the immersive natural
                landscapes designed for product exploration
              </p>
            </div>
            {/* User Flow Design */}
            <div data-id="element-776">
              <h3 className="text-2xl font-light text-stone-900 mb-8" data-id="element-777">
                User Flow Design
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start" data-id="element-778">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="md:col-span-2 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/colab%203d%20plan.png')} data-id="element-779">
                  <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-780">
                    <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/colab%203d%20plan.png" alt="Co-Lab 3D Immersive Website" className="w-full h-auto" data-id="element-781" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-782">
                      <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-783" />
                    </div>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/big%203d%20site%20plan.png')} data-id="element-784">
                  <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-785">
                    <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/big%203d%20site%20plan.png" alt="Co-Lab 3D Site Plan" className="w-full h-auto" data-id="element-786" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-787">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-788" />
                    </div>
                  </div>
                </motion.div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-789">
                3D immersive website user flow design featuring nature-inspired
                interactive environments
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Telegram miniApp */}
      <section className="py-20 px-6 bg-white" data-id="element-790">
        <div className="max-w-7xl mx-auto" data-id="element-791">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-792">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-793">
              Telegram miniApp: Gamified Wellness Journey
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-794">
              The Telegram miniApp creates ongoing user engagement through a
              "play to earn" mechanic. Users nurture a virtual tree that grows
              as they complete wellness tasks and healthy habits. This
              gamification strategy drives retention while providing valuable
              wellness tips, with earned rewards translating to discounts on
              Co-Lab products—creating a virtuous cycle between healthy living
              and brand loyalty.
            </p>
            {/* Main UI Overview Image */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer mb-12" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-lab_ready/image_2025-11-06_12-30-58.png')} data-id="element-795">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-796">
                <img src="https://storage.yandexcloud.net/maria-images/images/co-lab_ready/image_2025-11-06_12-30-58.png" alt="Telegram miniApp UI Overview" className="w-full h-auto" data-id="element-797" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-798">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-799" />
                </div>
              </div>
            </motion.div>
            {/* UI Screens Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-800">
              {[{
              image: 'https://storage.yandexcloud.net/maria-images/images/co-lab_ready/CARE%20(1).png',
              title: 'Care Dashboard',
              description: 'Main interface for wellness tracking and tree nurturing'
            }, {
              image: 'https://storage.yandexcloud.net/maria-images/images/co-lab_ready/AYURVEDIC%20CLOCK.png',
              title: 'Ayurvedic Clock',
              description: 'Daily rhythm guidance based on Ayurvedic principles'
            }, {
              image: 'https://storage.yandexcloud.net/maria-images/images/co-lab_ready/HEALING%20AFFIRMATIONS%20POPUP.png',
              title: 'Healing Affirmations',
              description: 'Interactive affirmation popup for daily wellness'
            }, {
              image: 'https://storage.yandexcloud.net/maria-images/images/co-lab_ready/HEALING%20AFFIRMATIONS%20(1).png',
              title: 'Affirmations Library',
              description: 'Collection of personalized healing affirmations'
            }, {
              image: 'https://storage.yandexcloud.net/maria-images/images/co-lab_ready/TASK%20CARD.png',
              title: 'Task Cards',
              description: 'Wellness tasks that earn rewards and grow your tree'
            }, {
              image: 'https://storage.yandexcloud.net/maria-images/images/co-lab_ready/FRIENDS.png',
              title: 'Friends & Community',
              description: 'Social features for sharing wellness journeys'
            }].map((screen, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage(screen.image)} data-id="element-801">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-802">
                    <img src={screen.image} alt={screen.title} className="w-full h-auto object-contain" data-id="element-803" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-804">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-805" />
                    </div>
                  </div>
                  <div className="mt-3" data-id="element-806">
                    <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-807">
                      {screen.title}
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-808">
                      {screen.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>
            <p className="text-stone-600 font-light mt-8 text-center italic" data-id="element-809">
              Complete UI system for the Telegram miniApp showing wellness
              tracking, task completion, and community features
            </p>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-810">
        <div className="max-w-5xl mx-auto" data-id="element-811">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-812">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-813">
              UX Direction & Strategy
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-814">
              <p data-id="element-815">
                As UX Director for Co-Lab, my approach centered on creating a
                cohesive multi-platform ecosystem where each touchpoint serves a
                distinct purpose while contributing to a unified brand
                experience. The challenge was to balance innovation with
                usability—making immersive 3D experiences accessible while
                maintaining the core e-commerce functionality.
              </p>
              <p data-id="element-816">
                The design strategy prioritized emotional connection over
                transactional efficiency. Rather than optimizing solely for
                conversion, we created experiences that resonate with users'
                wellness aspirations, understanding that brand loyalty in the
                wellness space comes from authentic connection and value
                alignment.
              </p>
              <p data-id="element-817">
                The gamification layer through the Telegram miniApp represents a
                paradigm shift in customer retention. By rewarding healthy
                behaviors with product discounts, we created a system where the
                brand becomes an active partner in users' wellness journeys
                rather than just a product provider. This approach transforms
                customers into engaged community members.
              </p>
              <p data-id="element-818">
                Throughout the project, extensive user research and iterative
                testing informed every design decision. The comprehensive mobile
                UX documentation reflects our commitment to considering every
                user scenario and ensuring seamless experiences across all
                interaction contexts.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}